import { getAsyncContextStrategy } from '../asyncContext/index.js';
import { getMainCarrier } from '../carrier.js';
import { getClient, getCurrentScope } from '../currentScopes.js';
import { isEnabled } from '../exports.js';
import { debug } from './debug-logger.js';
import { getActiveSpan, spanToTraceHeader, spanToTraceparentHeader } from './spanUtils.js';
import { getDynamicSamplingContextFromSpan, getDynamicSamplingContextFromScope } from '../tracing/dynamicSamplingContext.js';
import { dynamicSamplingContextToSentryBaggageHeader } from './baggage.js';
import { TRACEPARENT_REGEXP, generateSentryTraceHeader, generateTraceparentHeader } from './tracing.js';

/**
 * Extracts trace propagation data from the current span or from the client's scope (via transaction or propagation
 * context) and serializes it to `sentry-trace` and `baggage` values. These values can be used to propagate
 * a trace via our tracing Http headers or Html `<meta>` tags.
 *
 * This function also applies some validation to the generated sentry-trace and baggage values to ensure that
 * only valid strings are returned.
 *
 * If (@param options.propagateTraceparent) is `true`, the function will also generate a `traceparent` value,
 * following the W3C traceparent header format.
 *
 * @returns an object with the tracing data values. The object keys are the name of the tracing key to be used as header
 * or meta tag name.
 */
function getTraceData() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var client = options.client || getClient();
  if (!isEnabled() || !client) {
    return {};
  }
  var carrier = getMainCarrier();
  var acs = getAsyncContextStrategy(carrier);
  if (acs.getTraceData) {
    return acs.getTraceData(options);
  }
  var scope = options.scope || getCurrentScope();
  var span = options.span || getActiveSpan();
  var sentryTrace = span ? spanToTraceHeader(span) : scopeToTraceHeader(scope);
  var dsc = span ? getDynamicSamplingContextFromSpan(span) : getDynamicSamplingContextFromScope(client, scope);
  var baggage = dynamicSamplingContextToSentryBaggageHeader(dsc);
  var isValidSentryTraceHeader = TRACEPARENT_REGEXP.test(sentryTrace);
  if (!isValidSentryTraceHeader) {
    debug.warn('Invalid sentry-trace data. Cannot generate trace data');
    return {};
  }
  var traceData = {
    'sentry-trace': sentryTrace,
    baggage: baggage
  };
  if (options.propagateTraceparent) {
    var traceparent = span ? spanToTraceparentHeader(span) : scopeToTraceparentHeader(scope);
    if (traceparent) {
      traceData.traceparent = traceparent;
    }
  }
  return traceData;
}

/**
 * Get a sentry-trace header value for the given scope.
 */
function scopeToTraceHeader(scope) {
  var _scope$getPropagation = scope.getPropagationContext(),
    traceId = _scope$getPropagation.traceId,
    sampled = _scope$getPropagation.sampled,
    propagationSpanId = _scope$getPropagation.propagationSpanId;
  return generateSentryTraceHeader(traceId, propagationSpanId, sampled);
}
function scopeToTraceparentHeader(scope) {
  var _scope$getPropagation2 = scope.getPropagationContext(),
    traceId = _scope$getPropagation2.traceId,
    sampled = _scope$getPropagation2.sampled,
    propagationSpanId = _scope$getPropagation2.propagationSpanId;
  return generateTraceparentHeader(traceId, propagationSpanId, sampled);
}
export { getTraceData };